/**
 * Фильтр темы «Голоса клиента» (#2229, спека
 * docs/plans/2026-10-11-voice-report-layers.md §2.5, §3) — без React.
 *
 * Выбранная тема (`voiceTheme` в адресе) сужает каждый блок каждой вкладки до
 * разговоров этой темы. Сервер подтверждает фильтр эхом `themeFilter`: экран
 * верит числам, только когда эхо называет ту же тему. Нет эха (edge-api до
 * #2098 параметр отбросил) или эхо чужой темы — числа не показываются: общие
 * числа не выдаются за отфильтрованные.
 */
import {
  VOICE_THEME_FILTER_ERROR_CODES,
  type OrgTheme,
  type VoiceThemeFilterEcho,
  type VoiceThemeFilterErrorCode,
  type VoiceThemes,
} from '@aica/api-types/schemas';
import { passesProposalThreshold } from '@aica/core/voc/theme-proposals';

/**
 * Что ответ говорит о фильтре:
 * - `none` — тема не выбрана, ответ без фильтра;
 * - `applied` — эхо называет выбранную тему: числа — её разговоров;
 * - `pending` — ответа ещё нет или это прежний ответ (placeholder): его
 *   числа другой темы или другого окна (период, проекты, категория), экран ждёт;
 * - `unavailable` — ответ пришёл, а эха выбранной темы в нём нет: фильтр не
 *   применён, числа не показываются.
 */
export type ThemeFilterVerdict =
  | { kind: 'none' }
  | { kind: 'applied'; echo: VoiceThemeFilterEcho }
  | { kind: 'pending' }
  | { kind: 'unavailable' };

/** What the verdict reads of a narrowed query. */
export interface ThemeFilterAnswer {
  data: { themeFilter?: VoiceThemeFilterEcho } | undefined;
  isPlaceholderData?: boolean;
}

export function themeFilterVerdict(
  voiceTheme: string | undefined,
  { data, isPlaceholderData = false }: ThemeFilterAnswer
): ThemeFilterVerdict {
  if (data === undefined) return voiceTheme ? { kind: 'pending' } : { kind: 'none' };
  const echo = data.themeFilter;
  if (voiceTheme === undefined) {
    // An echo without a theme asked: a narrowed answer kept while the filter
    // is lifted — not the whole stream's numbers.
    if (echo === undefined) return { kind: 'none' };
    return isPlaceholderData ? { kind: 'pending' } : { kind: 'unavailable' };
  }
  // A placeholder is the previous key's answer: even with the same theme its
  // numbers are another window's, so it never stands for the selected filter.
  if (isPlaceholderData) return { kind: 'pending' };
  if (echo?.code === voiceTheme) return { kind: 'applied', echo };
  return { kind: 'unavailable' };
}

/**
 * One verdict for a tab over its narrowed queries: one answer without the
 * filter is enough to hold back the whole tab's numbers; one still loading —
 * the tab waits; otherwise the first echo stands for the tab (the answers of
 * one tab count one scope, so their echoes agree).
 */
export function combineThemeFilterVerdicts(
  verdicts: readonly ThemeFilterVerdict[]
): ThemeFilterVerdict {
  if (verdicts.some((verdict) => verdict.kind === 'unavailable')) {
    return { kind: 'unavailable' };
  }
  if (verdicts.some((verdict) => verdict.kind === 'pending')) return { kind: 'pending' };
  return verdicts.find((verdict) => verdict.kind === 'applied') ?? { kind: 'none' };
}

/**
 * Why a tab whose blocks the filter holds back has nothing to export
 * (`VoiceExportMenu`): still loading — the button waits; an answer without the
 * selected theme's numbers — the menu says so. Without a theme selected a
 * narrowed answer is one kept while the filter is lifted: on its way out, not
 * «no numbers under the theme».
 */
export function themeFilterExportUnavailable(
  verdict: ThemeFilterVerdict,
  voiceTheme: string | undefined,
  blocksShown: boolean
): 'pending' | 'noNumbers' | null {
  if (blocksShown) return null;
  return verdict.kind === 'pending' || voiceTheme === undefined ? 'pending' : 'noNumbers';
}

/**
 * The filter's refusal (`{ error, code }`): 403 `themes_disabled` — the
 * organization switched themes off; 400 `theme_filter_invalid` — the code is
 * not an active theme of the set (archived, unknown). Anything else is `null`:
 * not the filter's matter.
 */
export function themeFilterRefusalCode(error: unknown): VoiceThemeFilterErrorCode | null {
  const failure = error as { status?: unknown; data?: unknown } | null;
  if (failure?.status !== 403 && failure?.status !== 400) return null;
  const code = (failure.data as { code?: unknown } | null | undefined)?.code;
  const known = VOICE_THEME_FILTER_ERROR_CODES.find((candidate) => candidate === code);
  if (known === undefined) return null;
  // Each code has its own status: a 400 «themes_disabled» is not the gate's.
  return (known === 'themes_disabled') === (failure.status === 403) ? known : null;
}

/**
 * The set's own refusal of the selected theme, where no request asks the
 * server (#2238): the list answered and holds no active theme by that code —
 * archived, reserved or unknown, what the server refuses with 400
 * `theme_filter_invalid` — and it is not a theme of the demo set the window
 * shows (a demo row: the demo set is not in the organization's own list). The
 * list or the rows not read yet (or failed) is no refusal.
 */
export function themeListRefusalCode(
  voiceTheme: string | undefined,
  themes: readonly OrgTheme[] | undefined,
  rows: readonly { code: string; demo?: boolean }[] | undefined
): VoiceThemeFilterErrorCode | null {
  if (voiceTheme === undefined || themes === undefined || rows === undefined) return null;
  const active =
    themes.some((theme) => theme.code === voiceTheme && theme.status === 'active') ||
    rows.some((row) => row.demo === true && row.code === voiceTheme);
  return active ? null : 'theme_filter_invalid';
}

/**
 * The window's fresh rows do not hold the selected theme (#2902). The rows are
 * the active themes of the window in the demo mode the screen asks for, so
 * this is a doubt, not a verdict: a list inside its `staleTime` may still call
 * the theme active after another session archived it, and only a read of the
 * list since the tab opened settles it (`themeListRefusalCode`). The rows not
 * read yet say nothing.
 */
export function themeRowsLackTheme(
  voiceTheme: string | undefined,
  rows: readonly { code: string }[] | undefined
): boolean {
  return (
    voiceTheme !== undefined &&
    rows !== undefined &&
    !rows.some((row) => row.code === voiceTheme)
  );
}

/**
 * The theme an export is filed under (§2.2): the file name gets its code, the
 * printed header says whose conversations these are, and without a comparable
 * previous window the export carries no previous numbers or trends either.
 */
export interface VoiceExportTheme {
  code: string;
  name: string;
  prevComparable: boolean;
}

/** What a file exported under a theme carries in its name — one rule for the CSV and print. */
export function themeFileSuffix(theme: VoiceExportTheme | undefined): string {
  return theme ? `-theme-${theme.code}` : '';
}

/** A trend not drawn: the previous window of the theme is not comparable. */
export const NO_TREND = { kind: 'none' } as const;

/** A theme the picker offers. */
export interface ThemeFilterOption {
  code: string;
  name: string;
}

/**
 * The themes the picker offers — the whole set's rows (the filter never
 * narrows the themes block, §2.5), each above the threshold of a new theme
 * (#845, Р3: ≥ 10 conversations and ≥ 3 % of the row's window): below it the
 * blocks under the filter would all be «too few conversations». The selected
 * theme stays in the list even below the threshold — the period may have
 * moved under it, and the picker must still name what is applied.
 */
export function themeFilterOptions(
  themes: VoiceThemes | undefined,
  selected: string | undefined,
  /** The selected theme's name when its row is not in the answer (the set's). */
  selectedName: string | undefined = selected
): ThemeFilterOption[] {
  const rows = themes?.rows ?? [];
  const options = rows
    .filter(
      (row) =>
        row.code === selected || passesProposalThreshold(row.calls, row.callsInWindow)
    )
    .map((row) => ({ code: row.code, name: row.name }));
  if (selected !== undefined && !options.some((option) => option.code === selected)) {
    options.push({ code: selected, name: selectedName ?? selected });
  }
  return options;
}

/** Whole days since the theme's base (`since`), never negative. */
export function daysSince(since: string, now: Date): number {
  const elapsed = now.getTime() - new Date(since).getTime();
  return Number.isFinite(elapsed) ? Math.max(0, Math.floor(elapsed / 86_400_000)) : 0;
}
